import { useMemo, useState } from "react";
import type {
  RoomMemberProgress,
  RoomProgressResponse,
} from "@/lib/clubs/rooms";
import { NudgeDialog, type NudgeMode } from "./NudgeDialog";
import styles from "./FacilitatorDashboard.module.scss";

const NUDGE_DAILY_CAP = 5;

export type ProgressStatus = "ahead" | "on_pace" | "behind" | "stalled";

const PROGRESS_LABEL: Record<ProgressStatus, string> = {
  ahead: "Ahead",
  on_pace: "On pace",
  behind: "Behind",
  stalled: "Stalled",
};

export interface FacilitatorDashboardProps {
  clubName: string;
  progress: RoomProgressResponse;
  bookTitle: string | null;
  /** Number of nudges this user has sent in the last 24h. */
  nudgesSentToday: number;
  /** True after the server returned 429 — immediately disables actions. */
  isCapped: boolean;
  isNudging: boolean;
  nudgeError: Error | null;
  onSendNudges: (
    receiverIds: string[],
    message: string,
    onSent: () => void,
  ) => void;
}

type SortKey = "status" | "lastActive" | "progress";

interface MemberRow extends RoomMemberProgress {
  status: ProgressStatus;
}

function classifyStatus(
  member: RoomMemberProgress,
  unlockedSegments: number,
): ProgressStatus {
  if (!member.lastActivity) return "stalled";
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const lastActive = new Date(member.lastActivity).getTime();
  if (lastActive < sevenDaysAgo) return "stalled";
  if (member.segmentsReadCount >= unlockedSegments) {
    return member.segmentsReadCount > unlockedSegments ? "ahead" : "on_pace";
  }
  if (member.segmentsReadCount < Math.max(0, unlockedSegments - 1)) {
    return "behind";
  }
  return "behind";
}

function statusWeight(s: ProgressStatus) {
  return { stalled: 4, behind: 3, on_pace: 2, ahead: 1 }[s];
}

export function FacilitatorDashboard({
  clubName,
  progress,
  bookTitle,
  nudgesSentToday,
  isCapped,
  isNudging,
  nudgeError,
  onSendNudges,
}: FacilitatorDashboardProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortKey>("status");
  const [dialogState, setDialogState] = useState<{
    mode: NudgeMode;
    receiverIds: string[];
  } | null>(null);

  const remaining = Math.max(0, NUDGE_DAILY_CAP - nudgesSentToday);
  const cappedNow = isCapped || remaining <= 0;

  const rows: MemberRow[] = useMemo(() => {
    const classified = progress.members.map((m) => ({
      ...m,
      status: classifyStatus(m, progress.unlockedSegments),
    }));

    if (sort === "status") {
      return [...classified].sort(
        (a, b) => statusWeight(b.status) - statusWeight(a.status),
      );
    }
    if (sort === "lastActive") {
      return [...classified].sort((a, b) => {
        const aT = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
        const bT = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
        return aT - bT; // oldest first (stragglers up)
      });
    }
    return [...classified].sort(
      (a, b) => a.segmentsReadCount - b.segmentsReadCount,
    );
  }, [progress, sort]);

  const allRowIds = rows.map((r) => r.userId);
  const allSelected =
    allRowIds.length > 0 && allRowIds.every((id) => selected.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allRowIds));
    }
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const stalledCount = rows.filter((r) => r.status === "stalled").length;
  const selectedCount = selected.size;

  const openSingleDialog = (userId: string) => {
    setDialogState({ mode: "single", receiverIds: [userId] });
  };

  const openBatchDialog = () => {
    if (selectedCount === 0) return;
    setDialogState({ mode: "batch", receiverIds: [...selected] });
  };

  const handleDialogSubmit = (message: string) => {
    if (!dialogState) return;
    onSendNudges(dialogState.receiverIds, message, () => {
      // Clear selection on batch success so the table shows fresh state
      if (dialogState.mode === "batch") setSelected(new Set());
      setDialogState(null);
    });
  };

  const dialogMembers = dialogState
    ? dialogState.receiverIds.map((id) => {
        const m = progress.members.find((x) => x.userId === id);
        return {
          id,
          displayName: m?.user.displayName ?? "Member",
        };
      })
    : [];

  const batchOverCap = cappedNow || selectedCount > remaining;

  return (
    <div className={styles.shell}>
      <header className={styles.head}>
        <div>
          <p className={styles.eyebrow}>
            {clubName} · {progress.roomName}
          </p>
          <h1 className={styles.title}>Facilitator dashboard</h1>
          <p className={styles.sub}>
            {bookTitle ? `${bookTitle} · ` : ""}seg {progress.unlockedSegments}{" "}
            of {progress.totalSegments}
            {progress.behindCount > 0
              ? ` · ${progress.behindCount} member${progress.behindCount === 1 ? "" : "s"} behind pace`
              : ""}
          </p>
        </div>
        <div className={styles.headRight}>
          <span
            className={`${styles.quotaPill} ${cappedNow ? styles.quotaPillCapped : ""}`}
            data-testid="nudge-quota"
            aria-live="polite"
          >
            {cappedNow ? (
              <>
                <strong>0/{NUDGE_DAILY_CAP}</strong> nudges left — daily cap
                reached
              </>
            ) : (
              <>
                <strong>
                  {remaining}/{NUDGE_DAILY_CAP}
                </strong>{" "}
                nudges left today
              </>
            )}
          </span>
        </div>
      </header>

      <div
        className={`${styles.batchBar} ${selectedCount > 0 ? styles.batchBarActive : ""}`}
      >
        <span className={styles.batchInfo}>
          {selectedCount > 0 ? (
            <>
              <strong>{selectedCount}</strong> selected
            </>
          ) : (
            <>Select members to send a batch nudge.</>
          )}
        </span>
        <span className={styles.batchActions}>
          {selectedCount > 0 && (
            <button
              type="button"
              className={styles.btnGhost}
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={selectedCount === 0 || batchOverCap}
            title={
              batchOverCap
                ? cappedNow
                  ? "You've hit your daily nudge cap."
                  : "Not enough nudges left in your daily quota."
                : undefined
            }
            onClick={openBatchDialog}
          >
            Nudge {selectedCount || ""}
            {selectedCount > 0 ? ` (${selectedCount} of ${remaining})` : ""}
          </button>
        </span>
      </div>

      {rows.length === 0 ? (
        <div className={styles.empty}>
          No members to display yet — invite people to join the room.
        </div>
      ) : (
        <table className={styles.table} data-testid="facilitator-table">
          <thead>
            <tr>
              <th className={styles.checkCol}>
                <input
                  type="checkbox"
                  aria-label="Select all members"
                  checked={allSelected}
                  onChange={toggleAll}
                />
              </th>
              <th>Member</th>
              <th
                className={styles.sortable}
                onClick={() => setSort("status")}
                aria-sort={sort === "status" ? "descending" : "none"}
              >
                Status
                {sort === "status" && (
                  <span className={styles.sortMark}>↓</span>
                )}
              </th>
              <th>Segment</th>
              <th
                className={styles.sortable}
                onClick={() => setSort("lastActive")}
                aria-sort={sort === "lastActive" ? "ascending" : "none"}
              >
                Last active
                {sort === "lastActive" && (
                  <span className={styles.sortMark}>↑</span>
                )}
              </th>
              <th
                className={`${styles.sortable} ${styles.numCol}`}
                onClick={() => setSort("progress")}
                aria-sort={sort === "progress" ? "ascending" : "none"}
              >
                Progress
                {sort === "progress" && (
                  <span className={styles.sortMark}>↑</span>
                )}
              </th>
              <th className={styles.actionCol} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isSelected = selected.has(row.userId);
              const totalSegments = progress.totalSegments;
              const fillPct =
                totalSegments > 0
                  ? Math.min(
                      100,
                      Math.round((row.segmentsReadCount / totalSegments) * 100),
                    )
                  : 0;
              return (
                <tr
                  key={row.userId}
                  className={isSelected ? styles.rowSelected : ""}
                >
                  <td className={styles.checkCol}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOne(row.userId)}
                      aria-label={`Select ${row.user.displayName ?? "member"}`}
                    />
                  </td>
                  <td>
                    <div className={styles.memberCell}>
                      <span
                        className={`${styles.avatar} ${
                          styles[`avatar_${row.status}`]
                        }`}
                        aria-hidden
                      >
                        {(row.user.displayName ?? "?")[0].toUpperCase()}
                      </span>
                      <div>
                        <span className={styles.memberName}>
                          {row.user.displayName ?? "Unknown member"}
                        </span>
                        <span className={styles.memberMail}>
                          joined {relativeDay(row.joinedAt)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`${styles.statusPill} ${
                        styles[`status_${row.status}`]
                      }`}
                    >
                      {PROGRESS_LABEL[row.status]}
                    </span>
                  </td>
                  <td className={styles.segCell}>
                    <span className={styles.segValue}>
                      {row.segmentsReadCount}/{progress.unlockedSegments}
                    </span>
                    <span className={styles.segBar} aria-hidden>
                      <span
                        className={styles.segBarFill}
                        style={{ width: `${fillPct}%` }}
                      />
                    </span>
                  </td>
                  <td className={styles.lastCell}>
                    {row.lastActivity ? relativeDay(row.lastActivity) : "—"}
                  </td>
                  <td className={styles.numCol}>{row.progressPercentage}%</td>
                  <td className={styles.actionCol}>
                    <button
                      type="button"
                      className={styles.linkBtn}
                      disabled={cappedNow}
                      onClick={() => openSingleDialog(row.userId)}
                      title={cappedNow ? "Daily nudge cap reached" : undefined}
                    >
                      Nudge
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <footer className={styles.foot}>
        <span>
          {rows.length} member{rows.length === 1 ? "" : "s"}
          {stalledCount > 0 ? ` · ${stalledCount} stalled` : ""}
        </span>
        <span>
          {sort === "status" && "Sorted by status (most behind first)"}
          {sort === "lastActive" && "Sorted by last active (oldest first)"}
          {sort === "progress" && "Sorted by progress (lowest first)"}
        </span>
      </footer>

      {dialogState && (
        <NudgeDialog
          mode={dialogState.mode}
          members={dialogMembers}
          remaining={remaining}
          isCapped={cappedNow}
          isSending={isNudging}
          error={nudgeError}
          onSubmit={handleDialogSubmit}
          onClose={() => setDialogState(null)}
        />
      )}
    </div>
  );
}

function relativeDay(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMs = Date.now() - then;
  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(diffMs / day);
  if (days < 1) return "today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "1 week ago";
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60) return "1 month ago";
  return `${Math.floor(days / 30)} months ago`;
}
